import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useBranding } from '../lib/branding.js';
import { api, ApiError } from '../lib/api.js';

/**
 * Self-service "forgot password". The server always answers `ok` (it never says
 * whether the account exists), so the confirmation is worded the same way; the
 * emailed link lands on {@link SetPasswordScreen}.
 */
export function ForgotPasswordScreen(): ReactNode {
  const [params] = useSearchParams();
  const { branding } = useBranding();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      await api.forgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(
        err instanceof ApiError ? (err.fieldMessage() ?? err.message) : 'Could not reach the server.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-wrap">
      <form className="card card-pad auth-card stack" onSubmit={submit}>
        <div>
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={branding.productName}
              style={{ maxHeight: 40, maxWidth: '70%', marginBottom: 4 }}
            />
          ) : (
            <h1 style={{ fontSize: 18 }}>{branding.productName}</h1>
          )}
          <p className="muted small" style={{ margin: '4px 0 0' }}>
            Reset your password
          </p>
        </div>
        {sent ? (
          <p className="small" style={{ margin: 0 }}>
            If an account exists for <strong>{email}</strong>, we&apos;ve emailed it a link to set a
            new password. If you normally use single sign-on,
            sign in with that instead.
          </p>
        ) : (
          <>
            <p className="muted small" style={{ margin: 0 }}>
              Enter your account&apos;s email and we&apos;ll send you a link to set a new password.
            </p>
            <label className="field">
              Email
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </label>
            {error ? <div className="form-error">{error}</div> : null}
            <button className="btn primary" type="submit" disabled={busy}>
              {busy ? 'Sending…' : 'Send reset link'}
            </button>
          </>
        )}
        <Link to="/login" className="small">
          Back to sign in
        </Link>
      </form>
    </div>
  );
}
